import React, { useRef, useCallback, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Typography, useTheme } from "@mui/material";
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "../funcionales/carousel/EmblaButtoms";
import { DotButton, useDotButton } from "../funcionales/carousel/EmblaDots";
import projects from "../clases/projects";
import CardProject from "./cardProjects";
import styles from "../../styles/emblema.module.css";
import { useLanguage } from "../../context/LanguageContext";
//-------------------------------------------
const TWEEN_FACTOR_BASE = 0.52;

const numberWithinRange = (number, min, max) =>
  Math.min(Math.max(number, min), max);

export default function CardsGrid({ id }) {
  const { language } = useLanguage();
  const projectsByLanguage = projects[language];
  const theme = useTheme();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
  });
  const tweenFactor = useRef(0);
  const tweenNodes = useRef([]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const setTweenNodes = useCallback((api) => {
    tweenNodes.current = api
      .slideNodes()
      .map((node) => node.querySelector(`.${styles["embla__slide__number"]}`));
  }, []);

  const setTweenFactor = useCallback((api) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
  }, []);

  const tweenScale = useCallback((api, eventName) => {
    const engine = api.internalEngine();
    const scrollProgress = api.scrollProgress();
    const slidesInView = api.slidesInView();
    const isScrollEvent = eventName === "scroll";

    api.scrollSnapList().forEach((snap, snapIndex) => {
      let diffToTarget = snap - scrollProgress;
      const slidesInSnap = engine.slideRegistry[snapIndex];

      slidesInSnap.forEach((slideIndex) => {
        if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

        if (engine.options.loop) {
          engine.slideLooper.loopPoints.forEach((loopItem) => {
            const target = loopItem.target();
            if (slideIndex === loopItem.index && target !== 0) {
              const sign = Math.sign(target);
              if (sign === -1) diffToTarget = snap - (1 + scrollProgress);
              if (sign === 1) diffToTarget = snap + (1 - scrollProgress);
            }
          });
        }

        const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
        const scale = numberWithinRange(tweenValue, 0, 1).toString();
        const tweenNode = tweenNodes.current[slideIndex];
        if (tweenNode) tweenNode.style.transform = `scale(${scale})`;
      });
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    setTweenNodes(emblaApi);
    setTweenFactor(emblaApi);
    tweenScale(emblaApi);

    emblaApi
      .on("reInit", setTweenNodes)
      .on("reInit", setTweenFactor)
      .on("reInit", tweenScale)
      .on("scroll", tweenScale)
      .on("slideFocus", tweenScale);
  }, [emblaApi, tweenScale]);

  const translations = {
    es: { title: "Portafolio" },
    en: { title: "Portfolio" },
  };

  return (
    <section id={id} className={styles.embla}>
      <div data-aos="fade-right">
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontFamily: theme.typography.fontFamily[0],
            fontWeight: 700,
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            background: theme.palette.secondary.main,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 5,
            textAlign: "center",
            textTransform: "uppercase",
          }}
        >
          {translations[language].title}
        </Typography>
        <div className={styles["embla__viewport"]} ref={emblaRef}>
          <div className={styles["embla__container"]}>
            {projectsByLanguage.map((project, index) => (
              <div className={styles["embla__slide"]} key={index}>
                <div className={styles["embla__slide__number"]}>
                  <CardProject
                    project={project}
                    onClick={() => emblaApi && emblaApi.scrollTo(index)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles["embla__controls"]}>
          <div className={styles["embla__buttons"]}>
            <PrevButton
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
            <NextButton
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </div>

          <div className={styles["embla__dots"]}>
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={`${styles.embla__dot} ${
                  index === selectedIndex ? styles["embla__dot--selected"] : ""
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
